import React, { useCallback, useMemo } from 'react';
import SimpleValueCell from 'components/ServicesTables/common/cells/SimpleValueCell';
import { translate } from 'components/Translation';
import CheckCell from 'components/ServicesTables/common/cells/CheckCell';
import ProgressBarCell from 'components/ServicesTables/common/cells/ProgressBarCell';
import { CustomAttribute, extractAttributeValue } from 'utils/ecommerce/services/extract';
import PriceCell from 'components/ServicesTables/common/cells/PriceCell';

interface ISimpleColumnProps {
	catalogEntryView: types.ecommerce.CatalogEntryView;
	maxValue: number;
	cells: string[];
}

const SimpleColumn = ({ catalogEntryView, maxValue, cells }: ISimpleColumnProps) => {
	const attributes = catalogEntryView?.attributes || [];
	const prices = catalogEntryView?.price || [];

	const priceCell = useMemo(() => {
		const price = {
			offer: prices.find((singlePrice) => singlePrice.usage === 'Offer'),
			display: prices.find((singlePrice) => singlePrice.usage === 'Display'),
		};
		return (
			<PriceCell
				price={price?.display.value || '-'}
				currency={price?.display.currency || ''}
				perMonth
				offerPrice={price.offer}
			/>
		);
	}, [prices]);

	const cell = useCallback(
		(attributeValue: any, cellName: string): React.ReactElement => {
			if (!attributeValue) {
				return <div />;
			}
			switch (cellName) {
				case CustomAttribute.GOBIZ_SMS_COUNT:
					return (
						<SimpleValueCell
							value={attributeValue}
							title={translate('services.table.SMS')}
							subtitle={translate('services.table.at.gobiz.destination')}
						/>
					);
				case CustomAttribute.GOBIZ_UNIFIED_ROAMING:
					return (
						<CheckCell
							checked={attributeValue}
							subtitle={translate('services.table.unified.roaming.rates')}
							title={translate('services.table.at.gobiz.destination.light')}
						/>
					);
				case CustomAttribute.GOBIZ_CALLS_MIN:
					return (
						<SimpleValueCell
							value={attributeValue}
							title={translate('services.table.receive.calls')}
							subtitle={translate('services.table.at.gobiz.destination')}
							backgroundColor="backgroundGray"
						/>
					);
				case CustomAttribute.MBB_SIZE_GLOBAL:
					return (
						<SimpleValueCell
							value={attributeValue}
							title={translate('services.table.data.allowance.global')}
							backgroundColor="backgroundGray"
						/>
					);
				case CustomAttribute.MBB_PACKAGE_SIZE:
					return (
						<ProgressBarCell
							value={attributeValue.split(' ')[0] ?? attributeValue}
							maxValue={maxValue}
							title={translate('services.table.data.allowance.oman')}
							currency={attributeValue.split(' ')[1] ?? 'GB'}
						/>
					);
				case CustomAttribute.MBB_PACKAGE_SIZE_GOBIZ:
					// eslint-disable-next-line no-case-declarations
					return (
						<ProgressBarCell
							value={attributeValue.split(' ')[0] ?? attributeValue}
							maxValue={maxValue}
							title={translate('services.table.data.allowance')}
							subtitle={translate('services.table.at.gobiz.destination')}
							backgroundColor="backgroundGray"
							currency={attributeValue.split(' ')[1] ?? 'GB'}
						/>
					);
				default:
					return <div />;
			}
		},
		[maxValue]
	);

	const cellsArray = useMemo(() => {
		return cells.map((cellName: string) => {
			const attributeValue = extractAttributeValue({ attributes }, cellName);
			return cell(attributeValue, cellName);
		});
	}, [cell, attributes, cells]);

	return (
		<>
			{cellsArray.map((singleCell, index) => (
				<div key={index}>{singleCell}</div>
			))}
			{priceCell}
		</>
	);
};

export default SimpleColumn;
